import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { SlidersHorizontal, ChevronRight, BarChart3, Layers, FlaskConical, History } from 'lucide-react'
import { PageHeader, fadeUp } from '@personic/shared'
import { buildBreadcrumbs } from '@/config/routes'
import { useCalibration } from '@/hooks/useCalibration'

export default function Overview() {
  const { data: calibration, isLoading } = useCalibration()
  const breadcrumbs = buildBreadcrumbs('/settings/calibration/overview')

  const sections = [
    { label: 'Dimensions', href: '/settings/calibration/dimensions', desc: 'Configure personality dimension weights', icon: BarChart3 },
    { label: 'Presets', href: '/settings/calibration/presets', desc: 'Manage calibration preset configurations', icon: Layers },
    { label: 'Testing', href: '/settings/calibration/test', desc: 'Test calibration with sample profiles', icon: FlaskConical },
    { label: 'History', href: '/settings/calibration/history', desc: 'View calibration change log', icon: History },
  ]

  return (
    <div>
      <PageHeader
        title="Tenant Calibration"
        description="Fine-tune personality profiling parameters for your organization's use case."
        breadcrumbs={breadcrumbs}
      />

      <div className="space-y-6 max-w-4xl">
        {/* Status */}
        <motion.div variants={fadeUp} initial="hidden" animate="visible" className="rounded-xl border border-warm-200 bg-white p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-lg bg-copper-50 flex items-center justify-center">
              <SlidersHorizontal className="w-5 h-5 text-copper-500" />
            </div>
            <div>
              <h2 className="font-heading font-semibold text-midnight-800">Calibration Status</h2>
              <p className="text-xs text-warm-500">
                {isLoading ? 'Loading...' : calibration ? 'Calibration configured' : 'Using default calibration'}
              </p>
            </div>
          </div>
        </motion.div>

        {/* Section cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {sections.map((section) => (
            <motion.div key={section.href} variants={fadeUp} initial="hidden" animate="visible">
              <Link
                to={section.href}
                className="flex items-center justify-between p-5 rounded-xl border border-warm-200 bg-white hover:border-copper-300 hover:shadow-sm transition-all group"
              >
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-lg bg-warm-50 flex items-center justify-center group-hover:bg-copper-50 transition-colors">
                    <section.icon className="w-4 h-4 text-warm-500 group-hover:text-copper-500 transition-colors" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-midnight-800 group-hover:text-copper-700">{section.label}</p>
                    <p className="text-xs text-warm-500 mt-0.5">{section.desc}</p>
                  </div>
                </div>
                <ChevronRight className="w-4 h-4 text-warm-300 group-hover:text-copper-500" />
              </Link>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  )
}
